import { NotificationSeverity } from '@/components/notifications/Notification';
import { apiErrorMessage } from '../../IntegrationRequests/config';
import type { Confirm } from '../../IntegrationRequests/hooks/useConfirmDialog';
import { useNotification } from '@/hooks';
import {
  useCreateIntegrationWebhooksMutation,
  useDeleteIntegrationWebhookMutation,
  useUpdateIntegrationWebhookMutation,
} from '../../../services';
import type {
  IntegrationWebhook,
  IntegrationWebhookCreatePayload,
  IntegrationWebhookUpdatePayload,
} from '../../../models/IntegrationWebhook';

const DELETE_CONFIRMATION = {
  title: 'Borrar webhook',
  message: 'Si el webhook ya mandó avisos queda desactivado para no perder el historial; si no, se borra.',
  confirmLabel: 'Borrar',
};

const useIntegrationWebhookActions = (confirm: Confirm) => {
  const { showNotification } = useNotification();
  const [createWebhooks, { isLoading: isCreating }] = useCreateIntegrationWebhooksMutation();
  const [updateWebhook, { isLoading: isUpdating }] = useUpdateIntegrationWebhookMutation();
  const [deleteWebhook, { isLoading: isDeleting }] = useDeleteIntegrationWebhookMutation();

  const run = async <T>(
    action: () => Promise<T>,
    successMessage: (result: T) => string,
    errorMessage: string
  ): Promise<boolean> => {
    try {
      const result = await action();
      showNotification(successMessage(result), NotificationSeverity.success);
      return true;
    } catch (error) {
      showNotification(apiErrorMessage(error, errorMessage), NotificationSeverity.error);
      return false;
    }
  };

  const create = (payload: IntegrationWebhookCreatePayload) =>
    run(
      () => createWebhooks(payload).unwrap(),
      (created) => (created.length === 1 ? 'Webhook creado' : `${created.length} webhooks creados`),
      'No se pudo crear el webhook'
    );

  const update = (webhookId: string, payload: IntegrationWebhookUpdatePayload) =>
    run(
      () => updateWebhook({ webhookId, payload }).unwrap(),
      () => 'Webhook actualizado',
      'No se pudo actualizar el webhook'
    );

  const toggleActive = (webhook: IntegrationWebhook) =>
    run(
      () => updateWebhook({ webhookId: webhook.id, payload: { active: !webhook.active } }).unwrap(),
      (updated) => (updated.active ? 'Webhook activado' : 'Webhook desactivado'),
      'No se pudo cambiar el webhook'
    );

  const remove = async (webhook: IntegrationWebhook) => {
    if (!(await confirm(DELETE_CONFIRMATION))) return false;
    return run(
      () => deleteWebhook(webhook.id).unwrap(),
      (result) =>
        result.result === 'deleted'
          ? 'Webhook eliminado'
          : 'El webhook ya tenía envíos: quedó desactivado',
      'No se pudo borrar el webhook'
    );
  };

  return {
    create,
    update,
    toggleActive,
    remove,
    isSaving: isCreating || isUpdating,
    isBusy: isCreating || isUpdating || isDeleting,
  };
};

export type IntegrationWebhookActions = ReturnType<typeof useIntegrationWebhookActions>;
export default useIntegrationWebhookActions;
